import axios from 'axios';
import Cookies from 'js-cookie';
import { encryptData, decryptData } from '../component/Crypto_Helpers/cryptoHelper';
import { initEncryptionSession } from '../encryptionSession';

const API_URL = import.meta.env.VITE_API_URL || 'https://health.orangehealth.online/';

const axiosInstance = axios.create({
  baseURL: API_URL,
});

// List of endpoints that should NOT be encrypted (e.g., login/session setup)
const publicUrls = [
  'pharmacy/LoginWeb',
  'pharmacy/ForgotPassword',
  'pharmacy/ResetPassword',
  'admin/create-session',
  'admin/logout',
  'admin/validate_uuid',
  'admin/check_nd_reset_password',
  'admin/forgot_password_check_mail'
];

// Request Interceptor: Handles Auth Token and Data Encryption
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = Cookies.get('tokenHeader') || localStorage.getItem('tokenHeader');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Skip encryption for public URLs, non-POST/PUT requests, or FormData uploads
    if (
      publicUrls.some((url) => config.url && config.url.includes(url)) ||
      !['post', 'put'].includes(config.method?.toLowerCase()) ||
      config.data instanceof FormData
    ) {
      return config;
    }

    // Encrypt request body if it exists and is not already wrapped in { objects: ... }
    if (config.data && !(config.data.objects && typeof config.data.objects === 'string')) {
      try {
        const aesKey = await initEncryptionSession();
        if (aesKey) {
          const encrypted = await encryptData(config.data, aesKey);
          config.data = { objects: encrypted };
        }
      } catch (err) {
        console.error('Encryption Interceptor Error:', err);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: Handles Auto-Decryption and 401s
axiosInstance.interceptors.response.use(
  async (response) => {
    // Skip decryption for public endpoints (e.g. create-session) to avoid
    // circular dependency: response interceptor → initEncryptionSession → axiosInstance → loop
    if (publicUrls.some((url) => response.config?.url?.includes(url))) {
      return response;
    }

    const aesKey = await initEncryptionSession();
    if (!aesKey) return response;

    // Case 1: Whole response is encrypted in "objects" field
    if (response.data && response.data.objects && typeof response.data.objects === 'string') {
      try {
        if (/^[A-Za-z0-9+/=]+$/.test(response.data.objects)) {
          const decrypted = await decryptData(response.data.objects, aesKey);
          if (decrypted) {
            response.data = decrypted;
          }
        }
      } catch (err) {
        console.warn('Decryption Interceptor Error (objects):', err);
      }
    }
    // Case 2: Only the "data" field is encrypted
    else if (response.data && (response.data.data || response.data.encryptedData) && typeof (response.data.data || response.data.encryptedData) === 'string') {
      try {
        const targetField = response.data.data ? 'data' : 'encryptedData';
        if (/^[A-Za-z0-9+/=]+$/.test(response.data[targetField])) {
          const decrypted = await decryptData(response.data[targetField], aesKey);
          if (decrypted) {
            response.data[targetField] = decrypted;
            if (targetField === 'encryptedData') {
              response.data.data = decrypted;
            }
          }
        }
      } catch (err) {
        console.warn('Decryption Interceptor Error (data/encryptedData):', err);
      }
    }
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear all session cookies and redirect to login
      Object.keys(Cookies.get()).forEach((c) => Cookies.remove(c));
      localStorage.clear();
      window.location.replace('/');
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
